import { useMutation } from '@tanstack/react-query';
import { PiuDataState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';

// Promise-aware trailing-only debounce. All callers within `wait` ms collapse
// into a single eventual fn(args-of-last-call) and share its resolved value.
function debounce<A extends unknown[], R>(fn: (...a: A) => Promise<R>, wait: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let resolvers: Array<(v: R) => void> = [];
  let rejecters: Array<(e: unknown) => void> = [];
  let lastArgs: A;
  return async (...args: A): Promise<R> => {
    lastArgs = args;
    return await new Promise<R>((resolve, reject) => {
      resolvers.push(resolve);
      rejecters.push(reject);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const rs = resolvers;
        const js = rejecters;
        resolvers = [];
        rejecters = [];
        timer = undefined;
        fn(...lastArgs).then(
          v => {
            rs.forEach(r => {
              r(v);
            });
          },
          e => {
            js.forEach(r => {
              r(e);
            });
          },
        );
      }, wait);
    });
  };
}

export interface UpsertProductSetResponse {
  id: number;
}

async function upsertPlgCompanyProductSet(companyId: string, piuDataState: PiuDataState) {
  const payload = { piuDataState };
  return await fetchOrThrow<UpsertProductSetResponse>(`/blanket/plg-company-product-set/${companyId}`, 'POST', payload);
}

export interface UpsertProductSetMutationParams {
  piuDataState: PiuDataState;
  companyId: string;
}

async function doUpsertPlgCompanyProductSet(params: UpsertProductSetMutationParams) {
  return await upsertPlgCompanyProductSet(params.companyId, params.piuDataState);
}

const debouncedUpsertPlgCompanyProductSet = debounce(doUpsertPlgCompanyProductSet, 1500);

export const UPSERT_PLG_PRODUCT_SET_MUTATION_KEY = ['upsert-plg-product-set'];

export function useUpsertProductSetMutation() {
  return useMutation<UpsertProductSetResponse, Error, UpsertProductSetMutationParams>({
    mutationKey: UPSERT_PLG_PRODUCT_SET_MUTATION_KEY,
    mutationFn: debouncedUpsertPlgCompanyProductSet,
  });
}
